import { BusinessAnalytics } from "@xepp/business-analytics-sdk";
import { PaymentOptions } from "../../pages/customer/buy-electricity/buy-electricity.types";
import { Transaction } from "../../model/transaction";
import { VendingStepStatus, VendingStepType } from "../../pages/customer/components/transaction-result-v2/types";
import { FieldValue, toAmount, withoutEmpty } from "./customFields";

/**
 * Buy Electricity events for the web app, mirroring the shared analytics event sheet.
 *
 * The flow and all eleven steps are seeded by the backend migration SeedPaymentVendFlow
 * (flow "payment_vend", activation step bill_payment_started, each *_failed rolling up to
 * its *_success), so the codes here have to stay in step with that migration.
 */
export const VEND_FLOW = "payment_vend";

export const VEND_EVENTS = {
	billPaymentStarted: "bill_payment_started",
	meterValidationSuccess: "meter_validation_success",
	meterValidationFailed: "meter_validation_failed",
	continueToPay: "continue_to_pay",
	paymentMethodSelected: "payment_method_selected",
	transactionAuthorizedSuccess: "transaction_authorized_success",
	transactionAuthorizedFailed: "transaction_authorized_failed",
	billingSuccess: "billing_success",
	billingFailed: "billing_failed",
	vendingSuccess: "vending_success",
	vendingFailed: "vending_failed",
} as const;

export type MeterType = "prepaid" | "postpaid";

/** Stable, platform-neutral codes — never the numeric enum value or the translated label. */
export const PAYMENT_METHOD_CODES: Record<PaymentOptions, string> = {
	[PaymentOptions.Card]: "card",
	[PaymentOptions.Wallet]: "wallet",
	[PaymentOptions.Mono]: "mono",
	[PaymentOptions.Bank]: "bank",
	[PaymentOptions.BorrowToken]: "borrow_token",
	[PaymentOptions.Cash]: "cash",
	[PaymentOptions.Transfer]: "transfer",
	[PaymentOptions.AskAFriendToPay]: "ask_a_friend",
};

/** Why billing ended without a completed payment, as the payment processors report it. */
export type BillingFailureReason = "payment_failed" | "verification_timeout";

/** Keeps the first and last three characters, the same shape the mobile app sends. */
export const maskMeterNumber = (meterNumber: string): string => {
	const value = (meterNumber ?? "").trim();
	if (value.length <= 6) return value;
	return `${value.slice(0, 3)}${"*".repeat(value.length - 6)}${value.slice(-3)}`;
};

const track = (event: string, fields: Record<string, FieldValue>): void => {
	BusinessAnalytics.track(event, VEND_FLOW, withoutEmpty(fields));
};

export const trackBillPaymentStarted = (meterType: MeterType): void => {
	track(VEND_EVENTS.billPaymentStarted, { meter_type: meterType });
};

export const trackMeterValidationSuccess = (meterNumber: string, meterType: MeterType): void => {
	track(VEND_EVENTS.meterValidationSuccess, { meter_id_masked: maskMeterNumber(meterNumber), meter_type: meterType });
};

export const trackMeterValidationFailed = (meterNumber: string, errorReason: string): void => {
	track(VEND_EVENTS.meterValidationFailed, { meter_id_masked: maskMeterNumber(meterNumber), error_reason: errorReason });
};

export const trackContinueToPay = (amount: string | number | null | undefined): void => {
	track(VEND_EVENTS.continueToPay, { amount: toAmount(amount) });
};

export const trackPaymentMethodSelected = (method: PaymentOptions): void => {
	track(VEND_EVENTS.paymentMethodSelected, { payment_method: PAYMENT_METHOD_CODES[method] });
};

export const trackTransactionAuthorizedSuccess = (method: PaymentOptions, amount: string | number | null | undefined): void => {
	track(VEND_EVENTS.transactionAuthorizedSuccess, { payment_method: PAYMENT_METHOD_CODES[method], amount: toAmount(amount) });
};

export const trackTransactionAuthorizedFailed = (method: PaymentOptions, errorReason: string): void => {
	track(VEND_EVENTS.transactionAuthorizedFailed, { payment_method: PAYMENT_METHOD_CODES[method], error_reason: errorReason });
};

export const trackBillingSuccess = (amount: string | number | null | undefined, transactionId: string): void => {
	track(VEND_EVENTS.billingSuccess, { amount: toAmount(amount), transaction_id: transactionId });
};

export const trackBillingFailed = (errorReason: string, transactionId: string): void => {
	track(VEND_EVENTS.billingFailed, { error_reason: errorReason, transaction_id: transactionId });
};

export const trackVendingSuccess = (meterType: MeterType, tokenGenerated: boolean, units: number | null | undefined): void => {
	track(VEND_EVENTS.vendingSuccess, { meter_type: meterType, token_generated: tokenGenerated, units: units ?? undefined });
};

export const trackVendingFailed = (meterType: MeterType, errorReason: string): void => {
	track(VEND_EVENTS.vendingFailed, { meter_type: meterType, error_reason: errorReason });
};

/** The backend VendingStatus enum as it appears on vendingDetails.vendingToken.vendingStatus. */
const VENDING_STATUS = { COMPLETED: 2, FAILED: 3, FAILED_WITH_REFUND: 4 } as const;

export type VendingOutcome = { kind: "success"; tokenGenerated: boolean; units: number | null } | { kind: "failed"; errorReason: string } | null;

/**
 * The vending outcome recorded on a fetched prepaid transaction, or null while token
 * generation is still in progress. A token present on the record counts as success even
 * before the status catches up; a failed status or a failed token-generation step counts as
 * failure, with the most recent timeline error as the reason.
 */
export const vendingOutcomeOf = (transaction: Transaction | null | undefined): VendingOutcome => {
	const details = transaction?.vendingDetails;
	if (!details) return null;
	const token = details.vendingToken;

	if (token?.token || token?.vendingStatus === VENDING_STATUS.COMPLETED) {
		return { kind: "success", tokenGenerated: !!token?.token, units: typeof token?.totalKwh === "number" ? token.totalKwh : null };
	}

	const statusFailed = token?.vendingStatus === VENDING_STATUS.FAILED || token?.vendingStatus === VENDING_STATUS.FAILED_WITH_REFUND;
	const stepFailed = details.historySteps?.some(
		step => Number(step.typeOfEvent) === VendingStepType.TOKEN_GENERATION && Number(step.subTypeOfEvent) === VendingStepStatus.FAILED
	);
	if (statusFailed || stepFailed) {
		const lastError = [...(details.tokenTimeline ?? [])].reverse().find(item => item.error)?.error;
		return { kind: "failed", errorReason: lastError ?? "token_generation_failed" };
	}

	return null;
};

/** Sends the vending outcome of a transaction when it is final; returns whether it was. */
export const trackVendingOutcome = (transaction: Transaction | null | undefined, meterType: MeterType = "prepaid"): boolean => {
	const outcome = vendingOutcomeOf(transaction);
	if (!outcome) return false;
	if (outcome.kind === "success") trackVendingSuccess(meterType, outcome.tokenGenerated, outcome.units);
	else trackVendingFailed(meterType, outcome.errorReason);
	return true;
};
